import type { DocumentOut, PageChoice } from "@/api";

/**
 * Seitenwahl (13.10.2026) — which page a reading reads.
 *
 * The reading chain reads one page: the first of what it is handed
 * (`lesekette4`, `pages[0]`). A six-page offer with a photo on page 1 was
 * read as the photo and ended at „Umriss von Hand“. So wherever more than one
 * page is on file, „read all of it“ would only be a silent choice of page 1 —
 * and a person chooses instead. Nothing is preselected (product rule „prüft,
 * bestätigt nicht“): no page is read that nobody pointed at.
 */

/** One page of one stored drawing, as a tile in the choice. */
export interface PageTile {
  documentId: string;
  /** 1-based, as the server counts. */
  page: number;
  /** Pages of its document — what „Seite 2 von 6“ says. */
  pages: number;
  filename: string;
}

/** A document's pages as the choice counts them: a broken count (`null`) is one page that may not show. */
function pagesOf(document: DocumentOut): number {
  return Math.max(1, document.pageCount ?? 1);
}

/** Every page of every readable drawing, in their order on file. */
export function pageTiles(drawings: readonly DocumentOut[]): PageTile[] {
  return drawings.flatMap((document) =>
    Array.from({ length: pagesOf(document) }, (_, index) => ({
      documentId: document.id,
      page: index + 1,
      pages: pagesOf(document),
      filename: document.originalFilename,
    })),
  );
}

/**
 * Whether a person has to say which page is read: more than one page on
 * file, in one document or across several. One page, one picture: read as
 * it is, no extra step.
 */
export function needsPageChoice(drawings: readonly DocumentOut[]): boolean {
  return pageTiles(drawings).length > 1;
}

/** What the server is told: exactly the chosen document, and its page. */
export function choiceOf(tile: Pick<PageTile, "documentId" | "page">): PageChoice {
  return { documentId: tile.documentId, page: tile.page };
}

export function sameTile(a: Pick<PageTile, "documentId" | "page"> | null, b: Pick<PageTile, "documentId" | "page">): boolean {
  return a !== null && a.documentId === b.documentId && a.page === b.page;
}

/**
 * The tile's name — read out on the radio and said again once chosen. With
 * one document the page alone; with several, the file first, so „Seite 1“
 * is never ambiguous.
 */
export function tileName(tile: PageTile, severalDocuments: boolean): string {
  const page = `Seite ${tile.page} von ${tile.pages}`;
  return severalDocuments ? `${tile.filename}, ${page}` : page;
}
